import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getErrorMessages,
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
} from "../../../services";
import { OrderType, PaginationType } from "../../../services/types";
import { Transaction } from "../../../services/transactions";
import { TransactionType } from "../../../utils";
import { FilterValues } from "../components/TransactionsFilters/types";
import { PeriodState } from "../TransactionsPage/types";
import { GetTransactionsProps, SortValueType } from "../types";
import { PAGE_SIZE } from "../../../utils/constants";

const EMPTY_PAGINATION: PaginationType = {
  page: 0,
  limit: PAGE_SIZE,
  hasNext: false,
  totalRows: 0,
};

interface UseTransactionsListProps {
  period: PeriodState;
  filters: FilterValues;
  debouncedSearch: string;
  isMonthPeriod: boolean;
  cashbackBuysOf?: number;
}

export interface UseTransactionsListReturn {
  transactions: Transaction[];
  pagination: PaginationType;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  loadMore: () => void;
  refetch: () => Promise<void>;
}

interface SortMapping {
  sortBy: SortValueType;
  orderBy: OrderType;
}

const SORT_MAP: Record<string, SortMapping> = {
  date_desc: {
    sortBy: SortValueType.TRANSACTION_DATE,
    orderBy: OrderType.DESCENDING,
  },
  date_asc: {
    sortBy: SortValueType.TRANSACTION_DATE,
    orderBy: OrderType.ASCENDING,
  },
  amount_desc: {
    sortBy: SortValueType.AMOUNT,
    orderBy: OrderType.DESCENDING,
  },
  amount_asc: {
    sortBy: SortValueType.AMOUNT,
    orderBy: OrderType.ASCENDING,
  },
};

// Base page (0) is a subscribed query that auto-refetches on a Transaction tag invalidation; later pages are lazy-fetched and appended, reset whenever the base page's fulfilledTimeStamp or filters change.
export const useTransactionsList = ({
  period,
  filters,
  debouncedSearch,
  isMonthPeriod,
  cashbackBuysOf,
}: UseTransactionsListProps): UseTransactionsListReturn => {
  const baseParams = useMemo<GetTransactionsProps>(() => {
    const isAllTime = period.dateRange === "all";
    const categoryId = filters.category
      ? Number(filters.category.value)
      : undefined;
    const accountId = filters.account
      ? Number(filters.account.value)
      : undefined;
    const sortMapping = SORT_MAP[filters.sort.value] ?? SORT_MAP.date_desc;

    const type: TransactionType[] = [filters.type.value as TransactionType];

    return {
      type,
      searchBy: debouncedSearch,
      dateRange: period.dateRange,
      year: isAllTime ? undefined : period.year,
      month: isMonthPeriod ? period.month : undefined,
      categoryId,
      accountId,
      cashbackBuysOf,
      sortBy: sortMapping.sortBy,
      orderBy: sortMapping.orderBy,
      page: 0,
      rowsPerPage: PAGE_SIZE,
    };
  }, [
    period,
    isMonthPeriod,
    filters.type,
    filters.category,
    filters.account,
    cashbackBuysOf,
    filters.sort,
    debouncedSearch,
  ]);

  const base = useGetTransactionsQuery(baseParams);
  const [fetchMore, lazyResult] = useLazyGetTransactionsQuery();

  const [appended, setAppended] = useState<Transaction[]>([]);
  const [nextPage, setNextPage] = useState(1);
  const [latestPagination, setLatestPagination] = useState(
    base.data?.pagination,
  );

  const paramsKey = useMemo(() => JSON.stringify(baseParams), [baseParams]);

  useEffect(() => {
    setAppended([]);
    setNextPage(1);
    setLatestPagination(base.data?.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.fulfilledTimeStamp, paramsKey]);

  const transactions = useMemo(
    () => [...(base.data?.transactions ?? []), ...appended],
    [base.data?.transactions, appended],
  );

  const pagination =
    latestPagination ?? base.data?.pagination ?? EMPTY_PAGINATION;

  const loadMore = useCallback(() => {
    if (lazyResult.isFetching || !pagination.hasNext) {
      return;
    }
    fetchMore({ ...baseParams, page: nextPage })
      .unwrap()
      .then((result) => {
        setAppended((prev) => [...prev, ...result.transactions]);
        setLatestPagination(result.pagination);
        setNextPage((prev) => prev + 1);
      })
      .catch(() => {
        // Global middleware shows the toast; nothing else to do here.
      });
  }, [
    fetchMore,
    baseParams,
    nextPage,
    pagination.hasNext,
    lazyResult.isFetching,
  ]);

  const refetch = useCallback(async () => {
    await base.refetch();
  }, [base]);

  const error = base.error
    ? (getErrorMessages(base.error as any)[0] ?? "Failed to load transactions.")
    : null;

  return {
    transactions,
    pagination,
    isLoading: base.isLoading || (base.isFetching && !base.data),
    isLoadingMore: lazyResult.isFetching,
    error,
    loadMore,
    refetch,
  };
};
